import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search } from "lucide-react";
import api from "@/lib/api";
import { CATEGORIES, CATEGORY_STYLES } from "@/lib/session";

export default function PrayerLibrary() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    api.get("/library").then(({ data }) => setItems(data));
  }, []);

  const filtered = (category) => items.filter((i) => i.category === category && i.title.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="min-h-screen bg-[var(--brand-bg)] dark:bg-[var(--brand-bg-dark)]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <h1 className="font-sacred text-3xl text-[var(--brand-navy)] dark:text-amber-100 mb-1">Prayer Library</h1>
        <p className="text-sm text-muted-foreground mb-6">Browse set prayers, devotionals, chants, hymns, and activities</p>

        <div className="relative mb-6 max-w-md">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search the library..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="library-search-input" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="library-columns">
          {CATEGORIES.map((cat) => {
            const style = CATEGORY_STYLES[cat];
            return (
              <div key={cat} className="rounded-lg border border-[var(--brand-border)] dark:border-[var(--brand-border-dark)] overflow-hidden flex flex-col">
                <div className={`px-3 py-2.5 font-display text-lg font-semibold ${style.header}`}>{cat}</div>
                <div className="p-2 space-y-2 bg-white dark:bg-[var(--brand-card-dark)] flex-1">
                  {filtered(cat).map((item) => (
                    <button key={item.id} onClick={() => setPreview(item)} className="w-full text-left border border-[var(--brand-border)] dark:border-[var(--brand-border-dark)] rounded-md p-2.5 hover:border-[var(--brand-gold)] hover:shadow-sm transition-all" data-testid={`library-item-${item.id}`}>
                      <p className="text-sm font-semibold">{item.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{item.body}</p>
                    </button>
                  ))}
                  {filtered(cat).length === 0 && <p className="text-xs text-muted-foreground text-center py-4">No results</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent data-testid="library-preview-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{preview?.title}</DialogTitle>
          </DialogHeader>
          <p className={`inline-block text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full border w-fit ${preview ? CATEGORY_STYLES[preview.category].badge : ""}`}>{preview?.category}</p>
          <p className="text-sm whitespace-pre-line mt-2">{preview?.body}</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
